import { BadgeColor } from 'types';
import { useAppDispatch } from 'store';
import { Badge } from 'components/badge';
import { useSelectedCloverDevice } from 'hooks';
import { Select } from 'components/select/select';
import { isSingleSelectValue } from 'utils/helpers';
import { setSelectedDevice } from 'store/slices/clover-devices';
import { findReader, ReaderStatus } from 'components/reader-status';

import * as Styles from './styles';
import { SelectCloverDeviceProps } from './types';
import { useEventReaders } from './use-event-readers';

export const SelectCloverDevice = ({
  event,
  label,
  className,
  showReaderStatus,
}: SelectCloverDeviceProps) => {
  const dispatch = useAppDispatch();
  const { deviceOptions, selectedDevice } = useSelectedCloverDevice(event);
  const readers = useEventReaders(event?.id, !showReaderStatus);

  const selectedValue = deviceOptions?.find(
    (value) => value.value === selectedDevice?.id
  );

  const isMultiDevices = deviceOptions?.length > 1;

  if (!isMultiDevices) {
    // One reader: the picker stays hidden, as before; the cart shows a small
    // chip "<name> · <word> ●" instead. Nothing when the status can't be read.
    const reader = showReaderStatus
      ? findReader(readers, selectedDevice?.id)
      : undefined;
    if (!reader) {
      return null;
    }
    return (
      <Styles.Chip className={className} data-testid="cart-reader-chip">
        <Badge
          bold
          smallFont
          noEllipsis
          styleType={BadgeColor.LightGrey}
          text={
            <ReaderStatus
              dotAfter
              reader={reader}
              testId="cart-reader"
              label={selectedDevice?.name || reader.name}
            />
          }
        />
      </Styles.Chip>
    );
  }

  return (
    <Select
      className={className}
      value={selectedValue}
      options={deviceOptions}
      placeholder="Select Device"
      label={label || 'Select Device'}
      onChange={(value) => {
        if (isSingleSelectValue(value)) {
          dispatch(
            setSelectedDevice({
              eventId: event.id,
              deviceId: Number(value?.value),
            })
          );
        }
      }}
      {...(showReaderStatus && {
        // Two or more readers: a dot beside each name.
        formatOptionLabel: (option) => {
          const reader = findReader(readers, option.value);
          return reader ? (
            <ReaderStatus
              dotAfter
              reader={reader}
              nameOnly={option.label}
              testId={`reader-option-${option.value}`}
            />
          ) : (
            option.label
          );
        },
      })}
    />
  );
};
